import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import { Repeat, Timer, FileSearch } from "lucide-react";
import { RCA_CATEGORIES } from "@/lib/rca";

const SEV_FILL = { SEV1: "#dc2626", SEV2: "#f97316", SEV3: "#f59e0b", SEV4: "#94a3b8" };
const fmtMin = (m) => (m == null ? "—" : m < 60 ? `${m}m` : m < 2880 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${Math.floor(m / 1440)}d ${Math.floor((m % 1440) / 60)}h`);
const monthLabel = (ym) => { const [y, m] = ym.split("-").map(Number); return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "short", year: "2-digit" }); };

function Panel({ title, children, testid, className = "" }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-xl p-5 ${className}`} data-testid={testid}>
      <h3 className="font-display font-semibold text-slate-900 mb-4 text-sm">{title}</h3>
      {children}
    </div>
  );
}

function HBar({ data, color = "#6366f1", height }) {
  if (!data.length) return <p className="text-sm text-slate-400 py-6 text-center">No data</p>;
  return (
    <ResponsiveContainer width="100%" height={height || Math.max(140, data.length * 34)}>
      <BarChart data={data} layout="vertical" margin={{ left: 0, right: 16 }}>
        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
        <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11, fill: "#475569" }} axisLine={false} tickLine={false} />
        <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
        <Bar dataKey="count" fill={color} radius={[0, 4, 4, 0]} barSize={18} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function Kpi({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-center gap-3" data-testid={testid}>
      <span className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center"><Icon className="w-4 h-4" /></span>
      <div><p className="text-xl font-display font-bold text-slate-900">{value}</p><p className="text-[11px] text-slate-500">{label}</p></div>
    </div>
  );
}

export default function RcaTrends() {
  const [months, setMonths] = useState("6");
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/rcas/trends", { params: { months } }).then((r) => setD(r.data)); }, [months]);
  if (!d) return <p className="text-sm text-slate-400 py-10">Loading trends…</p>;
  const cats = d.by_category.map((c) => ({ ...c, name: RCA_CATEGORIES[c.name] || "Not set" }));

  return (
    <div className="space-y-5" data-testid="rca-trends">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-slate-500">{d.total} RCA{d.total === 1 ? "" : "s"} in range</p>
        <Select value={months} onValueChange={setMonths}>
          <SelectTrigger className="h-9 w-44 bg-white" data-testid="trends-range-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="3">Last 3 months</SelectItem><SelectItem value="6">Last 6 months</SelectItem>
            <SelectItem value="12">Last 12 months</SelectItem><SelectItem value="0">All time</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Kpi icon={FileSearch} label="RCAs" value={d.total} testid="trends-kpi-total" />
        <Kpi icon={Timer} label="Avg time to detect" value={fmtMin(d.avg_ttd_min)} testid="trends-kpi-ttd" />
        <Kpi icon={Timer} label="Avg time to resolve" value={fmtMin(d.avg_ttr_min)} testid="trends-kpi-ttr" />
      </div>

      <Panel title="Repeat offenders — same client hit more than once" testid="trends-repeats">
        {d.repeats.length ? (
          <div className="divide-y divide-slate-100">
            {d.repeats.map((x, i) => (
              <div key={i} className="flex items-center gap-3 py-2 text-sm" data-testid={`repeat-row-${i}`}>
                <Repeat className="w-4 h-4 text-red-500 shrink-0" />
                <span className="flex-1 min-w-0 truncate"><b className="font-semibold text-slate-900">{x.client}</b> · {x.kind === "system" ? x.item : RCA_CATEGORIES[x.item] || x.item}
                  <span className="ml-1.5 text-[10px] uppercase tracking-wide text-slate-400">{x.kind === "system" ? "system" : "root cause"}</span></span>
                <span className="font-display font-bold text-red-600">{x.count}×</span>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-slate-400">No repeats in this range — nice.</p>}
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Panel title="RCAs by client" testid="trends-by-client"><HBar data={d.by_client} /></Panel>
        <Panel title="Most affected systems" testid="trends-by-system"><HBar data={d.by_system.slice(0, 10)} color="#0ea5e9" /></Panel>
        <Panel title="Root-cause categories" testid="trends-by-category"><HBar data={cats} color="#8b5cf6" /></Panel>
        <Panel title="Severity mix" testid="trends-by-severity">
          {d.by_severity.length ? (
            <div className="flex items-center gap-4">
              <ResponsiveContainer width="55%" height={180}>
                <PieChart><Pie data={d.by_severity} dataKey="count" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
                  {d.by_severity.map((s) => <Cell key={s.name} fill={SEV_FILL[s.name]} />)}</Pie><Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} /></PieChart>
              </ResponsiveContainer>
              <ul className="space-y-1.5 text-sm">{d.by_severity.map((s) => <li key={s.name} className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: SEV_FILL[s.name] }} />{s.name}<b className="ml-1">{s.count}</b></li>)}</ul>
            </div>
          ) : <p className="text-sm text-slate-400 py-6 text-center">No data</p>}
        </Panel>
      </div>

      <Panel title="RCAs per month" testid="trends-by-month">
        {d.by_month.length ? (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={d.by_month.map((m) => ({ ...m, label: monthLabel(m.name) }))}>
              <CartesianGrid vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={28} />
              <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]} barSize={28} />
            </BarChart>
          </ResponsiveContainer>
        ) : <p className="text-sm text-slate-400 py-6 text-center">No data</p>}
      </Panel>
    </div>
  );
}
